import OfferPartnershipLabel from 'components/Common/Labels/OfferPartnershipLabel'
import NumberRating from 'components/Common/NumberRating'
import SearchTileHighlightBanner from 'components/SearchKit/SearchTiles/shared/SearchTileHighlightBanner'
import SearchTileRowOverlineLocation from 'components/SearchKit/SearchTiles/shared/SearchTileRowOverlineLocation'
import TileCancellationPolicy from 'components/SearchV2/SearchTileCancellationPolicy/TileCancellationPolicy'
import Group from 'components/utils/Group'
import LabelGroup from 'LuxKit/Label/LabelGroup'
import ProductTypeLabel from 'LuxKit/Label/ProductTypeLabel'
import PromotionLabel from 'LuxKit/Label/PromotionLabel'
import type { ImageChangeHandler } from 'LuxKit/Carousel/BaseImageCarousel'
import { carouselImageImpressionEvent } from 'analytics/snowplow/events'
import { trackEvent } from 'analytics/analytics'
import { OFFER_TYPE_ALWAYS_ON, OFFER_TYPE_HOTEL, PRODUCT_TYPE_LIMITED_TIME_SPECIAL } from 'constants/offer'
import CurrencyContext from 'contexts/currencyContext'
import OfferUrlContext from 'contexts/OfferUrlContext'
import productLabels from 'i18n/productLabels'
import useOffer from 'hooks/Offers/useOffer'
import useIsOpenNewTabOfferClickEnabled from 'hooks/Offers/useIsOpenNewTabOfferClickEnabled'
import useOfferMetaData from 'hooks/Offers/useOfferMetaData'
import useOfferRating from 'hooks/Offers/useOfferRating'
import useHideAnytimePrices from 'hooks/Offers/useHideAnytimePrices'
import { useAppSelector } from 'hooks/reduxHooks'
import useAccommodationMapSearchPath from 'hooks/Search/useAccommodationMapSearchPath'
import useMapSearchOfferUrl from 'hooks/Search/useMapSearchOfferUrl'
import { useSearchPrices } from 'hooks/Search/useSearchPrices'
import useSearchTargetName from 'hooks/Search/useSearchTargetName'
import useUserHasLoyaltyBenefit from 'loyalty/hooks/useUserHasLoyaltyBenefit'
import useSearchPropertyRateLoyaltyPackageUpgrade from 'loyalty/hooks/useSearchPropertyRateLoyaltyPackageUpgrade'
import LoyaltyStayUpgradeBenefitMessageBanner from 'loyalty/components/LoyaltyStayUpgradeBenefitMessageBanner'
import HotelSearchTileAlternativeDatesBanner from './HotelSearchTileAlternativeDatesBanner'
import HotelSearchTileEarnablePoints from './HotelSearchTileEarnablePoints'
import ReturnFareFreeLabel from 'components/Common/Labels/ReturnFareFreeLabel'
import useCharterBundleReturnFareFree from 'hooks/CharterFlights/useCharterBundleReturnFareFree'
import HotelSearchTileFeatures from './HotelSearchTileFeatures'
import HotelSearchTileHighlightBanner from './HotelSearchTileHighlightBanner'
import HotelSearchTileLabels from './HotelSearchTileLabels'
import HotelSearchTileUnavailabilityBanner from './HotelSearchTileUnavailabilityBanner'
import ExperimentalHotelSearchTileFeatures from './ExperimentalHotelSearchTileFeatures'
import HotelSearchTilePriceStack from './HotelSearchTilePriceStack'
import useMetaSearchDistance from './useMetaSearchDistance'
import useGroupedHotelSearchTileSecondaryOffer from './useGroupedHotelSearchTileSecondaryOffer'
import {
  GroupedHotelSearchTileOptionCard,
  GroupedHotelSearchTileOptionCards,
} from './GroupedHotelSearchTileOptionCards'
import GroupedHotelSearchTileSecondaryOptionCard from './GroupedHotelSearchTileSecondaryOptionCard'
import { nonNullable } from 'lib/array/arrayUtils'
import { diffInDays } from 'lib/datetime/dateUtils'
import { isFlashHotelOffer } from 'lib/offer/offerTypes'
import { getPackageUniqueKey, isLPCTacticalEnabled } from 'lib/offer/offerUtils'
import { scheduleIsCurrent } from 'lib/offer/scheduleStatusUtils'
import { buildSearchParamsKey, getShouldUseSearchPrices } from 'lib/search/searchUtils'
import noop from 'lib/function/noop'
import getTasteOfGoldPromoCampaign from 'takeovers/tasteOfGoldTakeover/selectors/getTasteOfGoldPromoCampaign'
import isTasteOfGoldEligibleOffer from 'takeovers/tasteOfGoldTakeover/utils/isTasteOfGoldEligibleOffer'
import TasteOfGoldTileBenefitMessage from 'takeovers/tasteOfGoldTakeover/components/TasteOfGoldTileBenefitMessage'
import { useCurrentSiteTakeover } from 'takeovers/utils/hooks/useCurrentSiteTakeover'
import BookmarkButton from 'tripPlanner/components/Bookmark/BookmarkButton'
import { generalLog } from 'marketing/api'
import { reportLog } from 'services/userMonitoring'
import { rem } from 'polished'
import type { MouseEvent, ReactNode } from 'react'
import { useCallback, useContext, useEffect, useMemo } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { v4 as uuidv4 } from 'uuid'
import useAdditionalProductImpressionRegistration from './useAdditionalProductImpressionRegistration'
import _SearchTileBase, { _searchTileContainerQueryUp } from '../_SearchTileBase'
import _SearchTileHero from '../_SearchTileHero'
import SearchTileRowTitle from '../shared/SearchTileRowTitle'
import { searchTileContentSpacingStyles } from '../searchTileLayoutStyles'
import LtleTooltipWrapper from 'components/Common/Labels/LtleTooltipWrapper'
import OfferListEventsContext, { OfferListEvents } from 'components/OfferList/OfferListEventsContext'
import OfferDescriptionContainer from 'components/OfferList/OfferListTiles/OfferDescriptionContainer'
import type { SearchTileDensity, SearchTileTreatment } from 'components/SearchKit/SearchTiles/searchTileDensity'
import { getAccommodationSearchTileView } from './accommodationSearchTileView'

const MAX_ALTERNATIVE_PRIMARY_AMENITIES = 3

const Content = styled.div<{ $density: SearchTileDensity }>`
  grid-area: content;
  display: flex;
  flex-direction: column;
  min-width: 0;
  ${searchTileContentSpacingStyles}
`

const Header = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: ${rem(12)};
  align-items: start;
`

const RatingContainer = styled.div`
  display: none;

  ${_searchTileContainerQueryUp.tablet} {
    display: block;
  }
`

const Extension = styled.div`
  &:empty {
    display: none;
  }
`

interface Props {
  offer: App.HotelOffer | App.HotelOfferSummary
  filters?: App.OfferListFilters
  productClick?: (offer: App.AnyOffer, isRightClick?: boolean) => void
  onAdditionalProductImpressionChange?: (offer?: App.AnyOffer) => void
  showPerNightPricing?: boolean
  storeSearchTile?: boolean
  showDescription?: boolean
  eagerLoadFirstImage?: boolean
  /**
   * @default default
   */
  density?: SearchTileDensity
  /**
   * @default default
   */
  treatment?: SearchTileTreatment
}

function GroupedHotelSearchTile({
  offer,
  filters,
  productClick = noop,
  onAdditionalProductImpressionChange = noop,
  showPerNightPricing = false,
  storeSearchTile = false,
  showDescription = false,
  eagerLoadFirstImage,
  density = 'default',
  treatment = 'default',
}: Props) {
  const tileView = getAccommodationSearchTileView(density)
  const intl = useIntl()
  const showReturnFareFree = useCharterBundleReturnFareFree({ offer, filters })
  const isOpenNewTabOfferClickEnabled = useIsOpenNewTabOfferClickEnabled()
  const listId = useMemo(() => uuidv4(), [])
  const utmSource = useAppSelector(state => state.utm.source)
  const noGoogleAffiliations = utmSource !== 'google'
  const metaData = useOfferMetaData(offer.id, filters)
  const checkIn = filters?.checkIn ?? metaData?.suggestedTravelDates?.checkIn
  const checkOut = filters?.checkOut ?? metaData?.suggestedTravelDates?.checkOut
  const isSpecificSearch = !!(checkIn && checkOut && filters?.rooms)
  const queryParams = useAppSelector((state) => state.router.location.query)
  const fullPath = useAppSelector((state) => state.router.location.pathname + state.router.location.search)

  const [, fetchingOffer] = useOffer<App.Offer>(offer.id, {
    requireSummaryOnly: !isSpecificSearch,
  })

  const searchKey = useMemo(() => buildSearchParamsKey(checkIn, checkOut, filters?.rooms), [checkIn, checkOut, filters?.rooms])

  const bestMaybeAvailableRate = useAppSelector<App.OfferMaybeAvailableRate | undefined>((state) => state.offer.offerBestPrices[offer.id]?.[searchKey])
  const bestPriceError = useAppSelector<boolean>((state) => !!state.offer.offerPricesErrors[offer.id]?.[searchKey])

  const shouldUseRealSearchPrices = getShouldUseSearchPrices(filters)
  const duration = metaData?.pricing?.duration ?? offer.lowestPricePackage?.duration
  const hasFiltersSet = !!(checkIn && checkOut && filters?.rooms)

  const bestPackage = useMemo<App.HotelPackage | undefined>(() => {
    if (filters?.roomRateId && filters?.roomTypeId) {
      const nightDuration = checkIn && checkOut ? diffInDays(new Date(checkIn), new Date(checkOut)) : 1
      const highlightedPackage = offer.packages.find(pkg =>
        pkg.roomRate.id === filters?.roomRateId &&
        pkg.roomType.id === filters?.roomTypeId &&
        pkg.duration === nightDuration,
      )
      if (highlightedPackage && fullPath.includes('metasearch')) {
        const priceTotal = queryParams.priceTotal || 0
        generalLog({
          status: 'info',
          message: 'GoogleHotelSearchPage: highlightedPackage',
          payload: { ...queryParams, fullPath, priceReceived: highlightedPackage.price, priceDifference: Number(priceTotal) - highlightedPackage.price },
        })
        return highlightedPackage
      }
    }
    if (!shouldUseRealSearchPrices) {
      if (!hasFiltersSet || bestPriceError) return offer.lowestPricePackage
      if (bestMaybeAvailableRate?.rate && !fetchingOffer) {
        const bestPricePkg = offer.packages.find(pkg => pkg.uniqueKey === bestMaybeAvailableRate.rate.packageUniqueKey)
        if (!bestPricePkg) {
          reportLog('Missing best price package from offer', {
            offerId: offer.id,
            packages: offer.packages.map(pkg => pkg.uniqueKey),
            searchKey,
            bestPriceRate: bestMaybeAvailableRate.rate,
          })
        }
        return bestPricePkg ?? offer.lowestPricePackage
      }
    } else {
      const pkgId = metaData?.pricing?.lowestPricePackageId ?? offer.lowestPricePackage?.id

      if (!pkgId || !duration || !bestMaybeAvailableRate?.rate?.roomRateId) {
        return offer.lowestPricePackage
      }

      const pkg = offer.packages.find(pkg => pkg.uniqueKey === getPackageUniqueKey(pkgId, duration, bestMaybeAvailableRate.rate.roomRateId))
      return pkg ?? offer.lowestPricePackage
    }
  }, [shouldUseRealSearchPrices, filters, checkIn, checkOut, offer, hasFiltersSet, bestPriceError, bestMaybeAvailableRate, fetchingOffer, searchKey, metaData, duration, queryParams, fullPath])

  const correctBestPackage = useMemo<App.HotelPackage | undefined>(() => {
    if (filters?.roomRateId && filters?.roomTypeId) {
      const nightDuration = checkIn && checkOut ? diffInDays(new Date(checkIn), new Date(checkOut)) : 1
      const highlightedPackage = offer.packages.find(pkg =>
        pkg.roomRate.id === filters?.roomRateId &&
        pkg.roomType.id === filters?.roomTypeId &&
        pkg.duration === nightDuration,
      )
      if (highlightedPackage) {
        const priceShown = queryParams.priceShown || 0
        generalLog({
          status: 'info',
          message: 'GoogleHotelSearchPage: highlightedPackage',
          payload: { ...queryParams, fullPath, priceReceived: highlightedPackage.price, priceDifference: Number(priceShown) - highlightedPackage.price },
        })
        return highlightedPackage
      }
    }
    if (!shouldUseRealSearchPrices) {
      if (!hasFiltersSet || bestPriceError) return offer.lowestPricePackage
      if (bestMaybeAvailableRate?.rate && !fetchingOffer) {
        const bestPricePkg = offer.packages.find(pkg => pkg.uniqueKey === bestMaybeAvailableRate.rate.packageUniqueKey)
        if (!bestPricePkg) {
          reportLog('Missing best price package from offer', {
            offerId: offer.id,
            packages: offer.packages.map(pkg => pkg.uniqueKey),
            searchKey,
            bestPriceRate: bestMaybeAvailableRate.rate,
          })
        }
        return bestPricePkg ?? offer.lowestPricePackage
      }
    } else {
      const pkgId = metaData?.pricing?.lowestPricePackageId ?? offer.lowestPricePackage?.id
      const roomRateId = metaData?.pricing?.lowestPriceRoomRateId ?? bestMaybeAvailableRate?.rate?.roomRateId

      if (!pkgId || !duration || !roomRateId) return

      return offer.packages.find(pkg => pkg.uniqueKey === getPackageUniqueKey(pkgId, duration, roomRateId))
    }
  }, [shouldUseRealSearchPrices, filters, checkIn, checkOut, offer, hasFiltersSet, bestPriceError, bestMaybeAvailableRate, fetchingOffer, searchKey, metaData, duration, queryParams, fullPath])

  const cancellationPolicyType = bestPackage?.roomRate?.cancellationPolicy?.type
  const productType = (isLPCTacticalEnabled() && offer.hasTactical) ? PRODUCT_TYPE_LIMITED_TIME_SPECIAL : offer.productType
  const locations = useMemo<Array<string>>(() => nonNullable([offer.locationHeading, offer.locationSubheading]), [offer])
  const mapSearchPath = useAccommodationMapSearchPath()
  const mapUrl = useMapSearchOfferUrl(storeSearchTile, offer, filters, mapSearchPath)
  const distanceFromSearchTarget = metaData?.distance
  const searchTargetName = useSearchTargetName(filters)
  const isSpoofed = useAppSelector((state) => state.auth.account.isSpoofed)
  const showProductLabel = isSpoofed || productType !== OFFER_TYPE_ALWAYS_ON

  const isSoldOutFromSearch = shouldUseRealSearchPrices
    ? !metaData?.available
    : (hasFiltersSet && bestMaybeAvailableRate !== undefined && !bestMaybeAvailableRate.available)

  const isSoldOut = isSoldOutFromSearch
  const dispatchOfferListEvent = useContext(OfferListEventsContext)

  useEffect(() => {
    if (isSoldOut) {
      dispatchOfferListEvent({
        type: OfferListEvents.pricingCalculated,
        offerId: offer.id,
        availability: false,
        price: undefined,
        roomRateId: undefined,
        memberPrice: undefined,
        duration: undefined,
        extendedAvailability: metaData?.extendedAvailability,
      })
    }

    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [isSoldOut])

  const showSpoofHighlight = useMemo<boolean>(() => {
    return isSpoofed && !scheduleIsCurrent(offer.visibilitySchedule)
  }, [isSpoofed, offer.visibilitySchedule])

  const isAnytimeSearch = (!filters?.checkIn && !filters?.checkOut) && !filters?.flexibleNights
  const showHighlight = useMemo(() => {
    if (showSpoofHighlight) return true
    if (showDescription) return false

    if (!filters?.propertyId || filters?.searchNearby) return false
    return offer.property?.id === filters.propertyId
  }, [filters, offer, showSpoofHighlight, showDescription])

  const metaSearchSourcePropertyId = filters?.propertyId ?? filters?.showDistanceWithMetaOfferId
  const isMetaSearchSamePropertyMatch = useAppSelector((state) => {
    if (!fullPath.includes('metasearch')) return false
    if (filters?.searchNearby) return false
    if (!metaSearchSourcePropertyId) return false
    const details = state.offer.alternativeOffersForProperty[metaSearchSourcePropertyId]?.leOfferDetails
    return details?.some((d) => d.offerId === offer.id) ?? false
  })

  const showSpecificRoomRateAndRoomType = filters?.roomRateId && filters?.roomTypeId
  const bestAvailableRate = bestMaybeAvailableRate?.available && !showSpecificRoomRateAndRoomType ? bestMaybeAvailableRate.rate : undefined
  const { defaultPricing: tileDisplayPricing } = useSearchPrices({
    offer,
    pkg: bestPackage,
    rate: bestAvailableRate,
    duration: duration ?? 1,
    filters,
    useRealSearchPrices: shouldUseRealSearchPrices,
  })
  const hidePrice = useHideAnytimePrices(offer.type, metaData?.hidePricing, productType, filters)
  const offerSiteTakeover = useCurrentSiteTakeover({ offer })
  const isTasteOfGoldActive = !!useAppSelector(getTasteOfGoldPromoCampaign)
  const currency = useContext(CurrencyContext)
  const rating = useOfferRating(offer, metaData, fullPath.includes('metasearch'))
  const userHasUpgradesBenefitAvailable = useUserHasLoyaltyBenefit('hotel_upgrade')
  const isUpgradeAvailableForSelectedDates = isAnytimeSearch ? true : (metaData?.packagesWithAvailableUpgrade.length ?? 0) > 0
  const hasHotelUpgradablePackages = offer.loyalty.hasPackageTypeUpgrades || offer.loyalty.hasRoomTypeUpgrades
  const { available: hasPackageRateUpgrade } = useSearchPropertyRateLoyaltyPackageUpgrade(offer, {
    checkIn,
    checkOut,
    rooms: filters?.rooms,
    enabled: tileView.showUpgradeBanner,
  })
  const showTasteOfGoldBenefit =
    isTasteOfGoldActive &&
    isTasteOfGoldEligibleOffer(offer, currency, offer.property.geoData.countryCode?.toLowerCase() !== 'au', tileDisplayPricing.price)

  const isFlashHotelProductType = productType === OFFER_TYPE_HOTEL
  const productTypeLabelNode = useMemo<ReactNode>(() => {
    if (!isFlashHotelProductType && !showProductLabel) return null

    const label = <ProductTypeLabel productType={productType} />

    if (isFlashHotelProductType) {
      return (
        <LtleTooltipWrapper autoOpenOnce>
          {label}
        </LtleTooltipWrapper>
      )
    }

    return label
  }, [isFlashHotelProductType, showProductLabel, productType])

  const metaSearchDistanceToMainOffer = useMetaSearchDistance({ offer, filters })
  const primaryOfferUrl = useContext(OfferUrlContext)
  const {
    secondaryOffer,
    secondaryOfferMetaData,
    fetching: fetchingSecondaryOffer,
  } = useGroupedHotelSearchTileSecondaryOffer(offer.id, filters)

  useAdditionalProductImpressionRegistration(onAdditionalProductImpressionChange, secondaryOffer)

  const isAlternativeGroupedTile = metaData?.groupedItemType === 'alternative'
  const hasSecondaryOffer = !!secondaryOffer || fetchingSecondaryOffer
  const offerLinkTarget = isOpenNewTabOfferClickEnabled ? '_blank' : undefined

  const handleOptionCardClick = useCallback((event: MouseEvent<HTMLElement>) => {
    event.stopPropagation()
  }, [])
  const handlePrimaryOptionCardClick = useCallback((event: MouseEvent<HTMLElement>) => {
    event.stopPropagation()
    productClick?.(offer)
  }, [offer, productClick])

  const handleImageChange = useCallback<ImageChangeHandler>((index, image) => {
    if (offer && image) {
      trackEvent(
        carouselImageImpressionEvent(
          image,
          index + 1,
          'search-tile',
          listId,
          offer,
          undefined,
        ),
      )
    }
  }, [offer, listId])

  const primaryTitle = isAlternativeGroupedTile ?
      intl.formatMessage({
        id: '9jH0vVPiS_FpfyNqvK2O',
        defaultMessage: 'Available for your dates',
        description: 'Title for the available primary option card in an alternative grouped hotel search tile',
      }) :
      intl.formatMessage(productLabels.hotel)
  const primaryActionLabel = intl.formatMessage({
    id: 'wgbxIJoadg0wltSWEp8d',
    defaultMessage: 'View offer',
    description: 'Primary option card CTA label in the grouped hotel search result tile',
  })

  const primaryFeatures = !noGoogleAffiliations ?
      <HotelSearchTileFeatures
        offer={offer}
        offerMetaData={metaData}
        filters={filters}
        bestPackage={bestPackage}
        doNotOpenInclusionsModal={storeSearchTile}
        maxTotalInclusions={tileView.groupedHotelMaxTotalInclusions}
        maxAmenitiesToShow={MAX_ALTERNATIVE_PRIMARY_AMENITIES}
        hideFeatureTitle
        hideRoomBreakdown
        hideLuxPlusLabels
      /> :
      <ExperimentalHotelSearchTileFeatures
        offer={offer}
        offerMetaData={metaData}
        filters={filters}
        bestPackage={correctBestPackage}
        maxInclusionsToShow={tileView.groupedHotelMaxTotalInclusions}
        maxAmenitiesToShow={MAX_ALTERNATIVE_PRIMARY_AMENITIES}
        hideSecondaryLuxPlusPromos={!tileView.showSecondaryLuxPlusPromos}
        hideLuxPlusLabels
        hideUpgradableInclusionsLabel
        hideRoomBreakdown
      />
  const pricePoint = !isSoldOut && <HotelSearchTilePriceStack
    offer={offer}
    filters={filters}
    bestPackage={bestPackage}
    duration={duration ?? 1}
    bestRate={bestAvailableRate}
    hidePrice={hidePrice}
    showPerNightPricing={showPerNightPricing}
    hideIncludesTaxesAndFees={storeSearchTile || tileView.hideIncludesTaxesAndFees}
    disableCharterPricing
    showCompactPricing={tileView.useCompactPricing}
  />
  return <_SearchTileBase
    density={density}
    treatment={treatment}
    data-product={offer.productType}
    data-offer={offer.type}
    state={showHighlight ? 'highlighted' : 'default'}
    banner={<>
      {showDescription && filters && (
        <OfferDescriptionContainer
          filters={filters}
          offerId={offer.id}
        />
      )}
      {isMetaSearchSamePropertyMatch && <SearchTileHighlightBanner
        description={intl.formatMessage({
          id: 'a8iSwYFsIlZmMvff2y0H',
          defaultMessage: 'Same property, better deal',
          description: 'Banner shown above the flash offer tile (slot 2) on the metasearch page when we surface a flash deal for the same property the user landed on from Google Hotel Ads',
        })}
      />}
      {showHighlight && !isMetaSearchSamePropertyMatch && <HotelSearchTileHighlightBanner offer={offer} offerMetaData={metaData} filters={filters} />}
      {offerSiteTakeover?.searchPage?.tileBanner}
    </>}
  >
    <_SearchTileHero
      bookmarkAction={storeSearchTile ? undefined : <BookmarkButton offer={offer} withLabel="always" />}
      eagerLoadFirstImage={eagerLoadFirstImage}
      images={offer.images}
      productLabel={<LabelGroup>
        {productTypeLabelNode}
        <OfferPartnershipLabel offer={offer} />
        {filters?.showDistanceWithMetaOfferId && metaSearchDistanceToMainOffer && <PromotionLabel>{metaSearchDistanceToMainOffer}</PromotionLabel>}
      </LabelGroup>}
      onImageChange={handleImageChange}
    />
    <Content $density={density}>
      {(hasHotelUpgradablePackages || hasPackageRateUpgrade) && userHasUpgradesBenefitAvailable && tileView.showUpgradeBanner && <Extension>
        {hasHotelUpgradablePackages && <LoyaltyStayUpgradeBenefitMessageBanner offer={offer} upgradeNotAvailableForSelectedDates={!isUpgradeAvailableForSelectedDates} />}
        {!hasHotelUpgradablePackages && hasPackageRateUpgrade && <LoyaltyStayUpgradeBenefitMessageBanner offer={offer} showPackageUpgrades showRoomUpgrades={false} />}
      </Extension>}
      <Header>
        <Group direction="vertical" gap={4}>
          {tileView.showLocationOverline && <SearchTileRowOverlineLocation
            locations={locations}
            mapUrl={mapUrl}
            distance={distanceFromSearchTarget}
            searchTargetLabel={searchTargetName}
            nearbyPois={metaData?.nearbyPois}
            propertyId={offer.property?.id}
            showDistanceLabels={tileView.showLocationDistanceLabels}
          />}
          <SearchTileRowTitle sponsored={metaData?.sponsored}>{offer.property.name}</SearchTileRowTitle>
          <HotelSearchTileLabels
            offer={offer}
            cancellationPolicyType={cancellationPolicyType}
            urgencyTagVariant={tileView.urgencyTagVariant}
            filters={filters}
            bestPackage={bestPackage}
            showUrgencyTags={!isSoldOut}
            timezoneOffset={offer.property.timezoneOffset}
          />
          {tileView.showCancellationPolicy && (!noGoogleAffiliations || isFlashHotelOffer(offer)) && !!cancellationPolicyType && <TileCancellationPolicy
            cancellationPolicyType={cancellationPolicyType}
            checkInDate={filters?.checkIn}
            timezoneOffset={offer.property.timezoneOffset}
            offerType={offer.type}
          />}
        </Group>
        {rating && <RatingContainer>
          <NumberRating
            rating={rating.score}
            total={rating.reviewsTotal}
            variant="small"
            numberPosition="end"
          />
        </RatingContainer>}
      </Header>
      {!storeSearchTile && tileView.showMeta && <Group direction="vertical" gap={8} noDisplayWhenEmpty>
        {showReturnFareFree && <ReturnFareFreeLabel />}
        <HotelSearchTileEarnablePoints
          offer={offer}
          bestPackage={bestPackage}
          bestAvailableRate={bestAvailableRate}
          filters={filters}
          metaData={metaData}
        />
        {showTasteOfGoldBenefit && <TasteOfGoldTileBenefitMessage />}
      </Group>}
      <GroupedHotelSearchTileOptionCards hasSecondaryOffer={hasSecondaryOffer}>
        <GroupedHotelSearchTileOptionCard
          title={primaryTitle}
          features={primaryFeatures}
          pricePoint={pricePoint}
          actionLabel={primaryActionLabel}
          to={primaryOfferUrl}
          target={offerLinkTarget}
          onClick={handlePrimaryOptionCardClick}
          isPrimary
        />
        <GroupedHotelSearchTileSecondaryOptionCard
          filters={filters}
          groupedItemType={metaData?.groupedItemType}
          bundledOfferId={metaData?.bundledOfferId}
          secondaryOffer={secondaryOffer}
          secondaryOfferMetaData={secondaryOfferMetaData}
          fetchingSecondaryOffer={fetchingSecondaryOffer}
          noGoogleAffiliations={noGoogleAffiliations}
          maxTotalInclusions={tileView.groupedHotelMaxTotalInclusions}
          showSecondaryLuxPlusPromos={tileView.showSecondaryLuxPlusPromos}
          hidePrice={hidePrice}
          showPricePoint={!isSoldOut}
          showPerNightPricing={showPerNightPricing}
          hideIncludesTaxesAndFees={storeSearchTile || tileView.hideIncludesTaxesAndFees}
          useCompactPricing={tileView.useCompactPricing}
          doNotOpenInclusionsModal={storeSearchTile}
          fallbackDuration={duration}
          onClick={handleOptionCardClick}
          productClick={productClick}
        />
      </GroupedHotelSearchTileOptionCards>
      <Extension>
        {metaData?.extendedAvailability && <HotelSearchTileAlternativeDatesBanner
          checkIn={metaData.extendedAvailability.checkIn}
          checkOut={metaData.extendedAvailability.checkOut}
        />}
        {isSoldOut && !metaData?.extendedAvailability &&
          <HotelSearchTileUnavailabilityBanner
            offer={offer}
            unavailableReason={metaData?.unavailableReason}
            filters={filters}
          />}
      </Extension>
    </Content>
  </_SearchTileBase>
}

export default GroupedHotelSearchTile
